'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  SquaresFour,
  CaretDown,
  CaretRight,
  ChatCircle,
  Trash,
  DotsThree,
  PencilSimple,
  TreeStructure,
  ClockCounterClockwise,
  Graph,
  Atom,
  Play,
  VideoCamera,
  ChartBar,
  FlowArrow,
  Crown,
  Lightning,
  SidebarSimple,
  FilePdf,
  FolderSimple,
  GraduationCap,
  ImageSquare,
  PlugsConnected,
  Robot,
  Brain,
  Trophy,
  GitBranch,
  FolderPlus,
  Folders,
  X,
  Check,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { GalexiaLogo } from '@/components/galexia-logo';
import { OPEN_SEARCH_EVENT } from '@/components/global-search';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/nextjs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ModeToggle } from '@/components/mode-toggle';
import { UpgradeModal } from '@/components/upgrade-modal';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface Session {
  id: string;
  title: string;
  vizType?: string;
  metadataStatus?: string;
  projectId?: string | null;
  /** Conversation branching — set when this session was forked from another. */
  parentSessionId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Projects feature — a named folder that groups chat sessions. */
interface Project {
  id: string;
  name: string;
  icon?: string | null;
  sessionCount: number;
}

function getVizIcon(vizType?: string) {
  switch (vizType) {
    case 'flowchart': return FlowArrow;
    case 'timeline': return ClockCounterClockwise;
    case 'concept_map': return Graph;
    case 'molecule': return Atom;
    case 'animation': return Play;
    case 'video': return VideoCamera;
    case 'diagram':
    case 'mermaid': return TreeStructure;
    default: return ChatCircle;
  }
}

interface SidebarProps {
  className?: string;
  currentSessionId?: string | null;
  onSessionSelect?: (sessionId: string | null) => void;
  onNewChat?: () => void;
  sessions?: Session[];
  onDeleteSession?: (sessionId: string) => void;
  onRenameSession?: (sessionId: string, title: string) => void | Promise<void>;
  userPlan?: 'free' | 'pro';
  creditsRemaining?: number;
  isAdmin?: boolean;
  /** Optional controlled collapse state. When provided, the parent owns
   *  the collapsed/expanded state and Sidebar mirrors it. Without it the
   *  Sidebar manages its own state (default: collapsed). */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  // Projects feature — group chat sessions into named folders.
  /** All of the caller's projects. `sessions` is assumed to already be
   *  filtered to `activeProjectId` by the parent (client-layout.tsx). */
  projects?: Project[];
  /** null/undefined = "All" (no filter active). */
  activeProjectId?: string | null;
  onProjectSelect?: (projectId: string | null) => void;
  onCreateProject?: (name: string, icon?: string) => Promise<Project | null>;
  onRenameProject?: (projectId: string, updates: { name?: string; icon?: string | null }) => Promise<void>;
  onDeleteProject?: (projectId: string) => Promise<void>;
  onAssignSessionProject?: (sessionId: string, projectId: string | null) => Promise<boolean>;
}

export function Sidebar({
  className,
  currentSessionId,
  onSessionSelect,
  onNewChat,
  sessions = [],
  onDeleteSession,
  onRenameSession,
  userPlan = 'free',
  creditsRemaining = 5,
  isAdmin = false,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  projects = [],
  activeProjectId = null,
  onProjectSelect,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onAssignSessionProject,
}: SidebarProps) {
  const router = useRouter();
  const [internalCollapsed, setInternalCollapsed] = useState(true);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;
  const setCollapsed = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === 'function' ? next(collapsed) : next;
    if (isControlled) {
      onCollapsedChange?.(resolved);
    } else {
      setInternalCollapsed(resolved);
      onCollapsedChange?.(resolved);
    }
  };
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [renameSession, setRenameSession] = useState<Session | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleteSession, setDeleteSession] = useState<Session | null>(null);

  // Projects feature state
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectIcon, setNewProjectIcon] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectIcon, setEditProjectIcon] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<Project | null>(null);

  const handleNewChat = () => {
    onNewChat?.();
    onSessionSelect?.(null);
    window.location.href = '/';
  };

  // Opens the Ctrl+K / Cmd+K command palette (GlobalSearch, mounted once in
  // client-layout.tsx) — decoupled via a window event so Sidebar doesn't need
  // an open/setOpen prop threaded down from AppLayout.
  const handleOpenSearch = () => {
    window.dispatchEvent(new CustomEvent(OPEN_SEARCH_EVENT));
  };

  const handleSessionClick = (sessionId: string) => {
    onSessionSelect?.(sessionId);
    router.push(`/chat/${sessionId}`);
  };

  const handleRenameOpen = (session: Session) => {
    setRenameSession(session);
    setRenameTitle(session.title);
  };

  const handleRenameSubmit = async () => {
    if (!renameSession) return;
    const trimmedTitle = renameTitle.trim();
    if (!trimmedTitle) return;
    setRenaming(true);
    try {
      await onRenameSession?.(renameSession.id, trimmedTitle);
      setRenameSession(null);
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteSession) return;
    await onDeleteSession?.(deleteSession.id);
    if (currentSessionId === deleteSession.id) router.push('/');
    setDeleteSession(null);
  };

  // --- Projects feature handlers -----------------------------------------
  const handleCreateProjectSubmit = async () => {
    const name = newProjectName.trim();
    if (!name) return;
    setCreatingProject(true);
    try {
      const created = await onCreateProject?.(name, newProjectIcon.trim() || undefined);
      if (created) {
        setCreateProjectOpen(false);
        setNewProjectName('');
        setNewProjectIcon('');
        onProjectSelect?.(created.id);
      }
    } finally {
      setCreatingProject(false);
    }
  };

  const handleEditProjectOpen = (project: Project) => {
    setEditProject(project);
    setEditProjectName(project.name);
    setEditProjectIcon(project.icon ?? '');
  };

  const handleEditProjectSubmit = async () => {
    if (!editProject) return;
    const name = editProjectName.trim();
    if (!name) return;
    setSavingProject(true);
    try {
      await onRenameProject?.(editProject.id, { name, icon: editProjectIcon.trim() || null });
      setEditProject(null);
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProjectConfirm = async () => {
    if (!deleteProjectTarget) return;
    await onDeleteProject?.(deleteProjectTarget.id);
    if (activeProjectId === deleteProjectTarget.id) onProjectSelect?.(null);
    setDeleteProjectTarget(null);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <motion.aside
        animate={{ width: collapsed ? 56 : 256 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        aria-label="Galexia primary navigation"
        className={cn(
          // Floating rounded card (reference language) instead of a flush
          // full-height rail — aligns with the rounded-2xl page panels.
          'relative m-2 mr-0 flex h-[calc(100vh-1rem)] flex-col overflow-hidden rounded-2xl border border-sidebar-border bg-sidebar',
          className,
        )}
      >
        {/* Header — logo + toggle */}
        <div className={cn(
          'flex items-center border-b border-sidebar-border shrink-0',
          collapsed ? 'justify-center px-0 py-4' : 'gap-2 px-4 py-4',
        )}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  aria-label="Expand sidebar"
                  aria-expanded={false}
                  className="group flex items-center justify-center rounded-lg hover:bg-accent transition-colors p-1"
                >
                  <span className="relative flex size-7 items-center justify-center">
                    <span className="absolute inset-0 flex items-center justify-center group-hover:opacity-0 transition-opacity">
                      <GalexiaLogo size={28} />
                    </span>
                    <SidebarSimple weight="bold" className="absolute size-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Open sidebar</TooltipContent>
            </Tooltip>
          ) : (
            <>
              <motion.div whileHover={{ scale: 1.05, rotate: 8 }} whileTap={{ scale: 0.95 }}>
                <GalexiaLogo size={32} />
              </motion.div>
              <span className="text-lg font-semibold tracking-wide flex-1">Galexia</span>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Collapse sidebar"
                aria-expanded={true}
              >
                <SidebarSimple weight="duotone" className="size-4" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {/* Actions */}
        <div className={cn('py-3 shrink-0 space-y-1', collapsed ? 'px-2' : 'px-3')}>
          {collapsed ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleNewChat}
                    aria-label="New chat"
                    className="flex items-center justify-center w-full h-9 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                  >
                    <Plus weight="bold" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">New Chat</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleOpenSearch}
                    aria-label="Search"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <MagnifyingGlass weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Search (⌘K)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/visualizations')}
                    aria-label="Visualizations"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <SquaresFour weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Visualizations</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/media')}
                    aria-label="Media Studio"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ImageSquare weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Media Studio</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/connections')}
                    aria-label="Connections"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <PlugsConnected weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Connections</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/library')}
                    aria-label="Library"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <FolderSimple weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Library</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/skills')}
                    aria-label="Skills"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <GraduationCap weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Skills</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/agents')}
                    aria-label="Agents"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Robot weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Agents</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/memory')}
                    aria-label="Memory"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Brain weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Memory</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => router.push('/documents')}
                    aria-label="Documents"
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <FilePdf weight="duotone" className="size-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Documents</TooltipContent>
              </Tooltip>
              {/* Community nav removed 2026-07-13 (user call: unowned surface,
                  dilutes focus). Route stays reachable at /community; restore
                  this entry if community becomes a priority again. */}
              {isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => router.push('/arena')}
                      aria-label="Arena"
                      className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Trophy weight="duotone" className="size-4" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Arena</TooltipContent>
                </Tooltip>
              )}
              {isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => router.push('/admin')}
                      aria-label="Admin"
                      className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ChartBar weight="duotone" className="size-4" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Admin</TooltipContent>
                </Tooltip>
              )}
            </>
          ) : (
            <>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="secondary"
                  className="w-full justify-start gap-2.5 h-10 font-medium shadow-sm hover:shadow-md"
                  onClick={handleNewChat}
                >
                  <Plus weight="bold" className="size-4 shrink-0" />
                  New chat
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={handleOpenSearch}
                >
                  <MagnifyingGlass weight="duotone" className="size-4 shrink-0" />
                  Search
                  <span className="ml-auto text-[11px] text-muted-foreground/50">⌘K</span>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/visualizations')}
                >
                  <SquaresFour weight="duotone" className="size-4 shrink-0" />
                  Visualizations
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/media')}
                >
                  <ImageSquare weight="duotone" className="size-4 shrink-0" />
                  Media Studio
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/connections')}
                >
                  <PlugsConnected weight="duotone" className="size-4 shrink-0" />
                  Connections
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/library')}
                >
                  <FolderSimple weight="duotone" className="size-4 shrink-0" />
                  Library
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/skills')}
                >
                  <GraduationCap weight="duotone" className="size-4 shrink-0" />
                  Skills
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/agents')}
                >
                  <Robot weight="duotone" className="size-4 shrink-0" />
                  Agents
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/memory')}
                >
                  <Brain weight="duotone" className="size-4 shrink-0" />
                  Memory
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                  onClick={() => router.push('/documents')}
                >
                  <FilePdf weight="duotone" className="size-4 shrink-0" />
                  Documents
                </Button>
              </motion.div>
              {/* Community removed from nav 2026-07-13 — see collapsed-rail note above. */}
              {isAdmin && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                    onClick={() => router.push('/arena')}
                  >
                    <Trophy weight="duotone" className="size-4 shrink-0" />
                    Arena
                  </Button>
                </motion.div>
              )}
              {isAdmin && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2.5 h-10 text-muted-foreground hover:text-foreground"
                    onClick={() => router.push('/admin')}
                  >
                    <ChartBar weight="duotone" className="size-4 shrink-0" />
                    Admin
                  </Button>
                </motion.div>
              )}
            </>
          )}
        </div>

        {/* Projects — group chats into named folders. Collapsed rail skips
            this entirely (same rule as the session list below). */}
        {!collapsed && (
          <div className="shrink-0 px-3 pt-1 pb-2 border-b border-sidebar-border/60">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[10px] font-semibold text-muted-foreground/55 uppercase tracking-[2px]">
                Projects
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setCreateProjectOpen(true)}
                    aria-label="New project"
                    className="p-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <FolderPlus weight="bold" className="size-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">New project</TooltipContent>
              </Tooltip>
            </div>

            <div className="space-y-0.5">
              <button
                type="button"
                onClick={() => onProjectSelect?.(null)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left transition-colors',
                  activeProjectId === null ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <Folders weight={activeProjectId === null ? 'fill' : 'duotone'} className="size-3.5 shrink-0" />
                <span className="flex-1 truncate">All chats</span>
              </button>

              {projects.map((project) => {
                const isActive = activeProjectId === project.id;
                return (
                  <div
                    key={project.id}
                    className={cn(
                      'group relative w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] cursor-pointer transition-colors',
                      isActive ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                    )}
                    onClick={() => onProjectSelect?.(project.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && onProjectSelect?.(project.id)}
                  >
                    <span className="size-3.5 shrink-0 flex items-center justify-center text-sm leading-none">
                      {project.icon || '📁'}
                    </span>
                    <span className="flex-1 truncate">{project.name}</span>
                    <span className="text-[10px] text-muted-foreground/60 tabular-nums group-hover:opacity-0 transition-opacity">
                      {project.sessionCount}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-1 opacity-0 group-hover:opacity-100 p-1 hover:bg-accent-foreground/10 rounded-md transition-all"
                          aria-label={`Open actions for ${project.name}`}
                        >
                          <DotsThree weight="bold" className="size-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleEditProjectOpen(project); }}>
                          <PencilSimple weight="bold" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={(e) => { e.preventDefault(); setDeleteProjectTarget(project); }}
                        >
                          <Trash weight="bold" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}

              {projects.length === 0 && (
                <p className="px-2 py-1 text-[11px] text-muted-foreground/55">
                  No projects yet — group chats to keep them organized.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recent Chats */}
        <div
          className={cn('flex-1 overflow-y-auto py-2', collapsed ? 'px-2 cursor-col-resize' : 'px-3')}
          onClick={collapsed ? () => setCollapsed(false) : undefined}
        >
          {!collapsed && (
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[10px] font-semibold text-muted-foreground/55 uppercase tracking-[2px]">
                {activeProjectId ? (projects.find((p) => p.id === activeProjectId)?.name ?? 'Recent') : 'Recent'}
              </span>
              <CaretDown weight="bold" className="size-3 text-muted-foreground/55" />
            </div>
          )}

          <div className="space-y-1">
            <AnimatePresence mode="popLayout">
              {sessions.length === 0 && !collapsed ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mx-2 my-2 px-3 py-4 rounded-lg border border-violet-500/15 bg-violet-500/[0.04] text-center"
                >
                  <p className="text-[12px] font-medium text-foreground/85">
                    No chats yet
                  </p>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground/65">
                    Ask anything in the input above and your conversation lands here.
                  </p>
                </motion.div>
              ) : (
                sessions.map((session, index) => {
                  const VizIcon = getVizIcon(session.vizType);
                  const isActive = currentSessionId === session.id;

                  if (collapsed) return null;

                  return (
                    <motion.div
                      key={session.id}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20, scale: 0.9 }}
                      transition={{ duration: 0.2, delay: index * 0.03, ease: 'easeOut' }}
                      layout
                    >
                      <div
                        onClick={() => handleSessionClick(session.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSessionClick(session.id)}
                        className={cn(
                          'relative w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-left group cursor-pointer',
                          'transition-colors duration-150 ease-out hover:bg-accent/70',
                          isActive && 'bg-accent',
                        )}
                      >
                        {/* Active "you are here" violet stripe — subtle, on the
                            left edge, only when the row is active. */}
                        {isActive && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full bg-violet-400"
                          />
                        )}
                        <VizIcon
                          weight="duotone"
                          className={cn(
                            'size-4 shrink-0 transition-colors',
                            isActive ? 'text-violet-300' : 'text-muted-foreground/70',
                          )}
                        />
                        <span className="flex-1 truncate">{session.title}</span>
                        {session.parentSessionId && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="shrink-0 text-muted-foreground/60 group-hover:opacity-0 transition-opacity"
                                aria-label="Branched from another chat"
                              >
                                <GitBranch weight="bold" className="size-3.5" aria-hidden="true" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right">Branched from another chat</TooltipContent>
                          </Tooltip>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-accent-foreground/10 rounded-md transition-all"
                              aria-label={`Open actions for ${session.title}`}
                            >
                              <DotsThree weight="bold" className="size-3.5 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleRenameOpen(session); }}>
                              <PencilSimple weight="bold" />
                              Rename chat
                            </DropdownMenuItem>
                            {projects.length > 0 && (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <Folders weight="bold" />
                                  Move to project
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-48">
                                  {projects.map((project) => (
                                    <DropdownMenuItem
                                      key={project.id}
                                      onSelect={(e) => { e.preventDefault(); onAssignSessionProject?.(session.id, project.id); }}
                                    >
                                      <span className="text-sm leading-none">{project.icon || '📁'}</span>
                                      <span className="flex-1 truncate">{project.name}</span>
                                      {session.projectId === project.id && <Check weight="bold" className="size-3.5" />}
                                    </DropdownMenuItem>
                                  ))}
                                  {session.projectId && (
                                    <DropdownMenuItem
                                      onSelect={(e) => { e.preventDefault(); onAssignSessionProject?.(session.id, null); }}
                                    >
                                      <X weight="bold" />
                                      Remove from project
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            )}
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={(e) => { e.preventDefault(); setDeleteSession(session); }}
                            >
                              <Trash weight="bold" />
                              Delete chat
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Plan / Upgrade */}
        <SignedIn>
          {!collapsed && userPlan === 'free' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mx-3 mb-3 p-3.5 rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] via-violet-500/[0.02] to-transparent"
            >
              <div className="flex items-center gap-2 mb-1">
                <Lightning weight="duotone" className="size-4 text-violet-300" />
                <span className="text-sm font-medium">Free plan</span>
              </div>
              {/* Credit progress — gives the number visual weight */}
              {(() => {
                const total = Math.max(creditsRemaining, 150);
                const used = Math.max(0, total - creditsRemaining);
                const pct = Math.min(100, (used / total) * 100);
                const empty = creditsRemaining <= 0;
                return (
                  <>
                    <div className="mb-2.5 flex items-center justify-between text-[11px] text-muted-foreground/75">
                      <span>{empty ? 'No credits left' : `${creditsRemaining} credits left`}</span>
                      <span className="text-muted-foreground/50">monthly grant</span>
                    </div>
                    <div className="h-1 mb-3 rounded-full bg-violet-500/10 overflow-hidden">
                      <div
                        className={`h-full transition-all ${empty ? 'bg-amber-400/80' : 'bg-gradient-to-r from-[#3B5BD9] via-[#8B5CF6] to-[#06B6D4]'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </>
                );
              })()}
              <Button
                size="sm"
                className="w-full gap-1.5 bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white shadow-[0_4px_16px_rgba(59,91,217,0.3)] hover:shadow-[0_4px_20px_rgba(59,91,217,0.45)] hover:scale-[1.01] transition-all"
                onClick={() => setShowUpgradeModal(true)}
              >
                <Crown weight="duotone" className="size-3.5" />
                Upgrade to Pro
              </Button>
            </motion.div>
          )}

          {!collapsed && userPlan === 'pro' && (
            <div className="mx-3 mb-3 px-3 py-2 rounded-lg border border-violet-500/20 bg-violet-500/[0.06]">
              <div className="flex items-center gap-2">
                <Crown weight="duotone" className="size-4 text-violet-300" />
                <span className="text-sm font-medium">Pro plan</span>
                <span className="ml-auto inline-flex items-center rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[1.5px] text-violet-300">
                  {creditsRemaining} cr
                </span>
              </div>
            </div>
          )}

          {collapsed && (
            <div className="px-2 mb-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => userPlan === 'free' ? setShowUpgradeModal(true) : undefined}
                    className="flex items-center justify-center w-full h-9 rounded-lg hover:bg-accent transition-colors"
                  >
                    <Crown
                      weight="fill"
                      className={cn('size-4', userPlan === 'pro' ? 'text-primary' : 'text-muted-foreground')}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {userPlan === 'pro' ? 'Pro Plan' : `Upgrade — ${creditsRemaining} visual${creditsRemaining !== 1 ? 's' : ''} left`}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </SignedIn>

        {/* Bottom — user + mode */}
        <div className={cn('border-t border-sidebar-border shrink-0', collapsed ? 'px-2 py-3' : 'p-3')}>
          <SignedIn>
            {collapsed ? (
              <div className="flex flex-col items-center gap-2">
                <UserButton afterSignOutUrl="/" appearance={{ elements: { avatarBox: 'size-8' } }} />
                <ModeToggle />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: 'size-10 ring-2 ring-sidebar-border' } }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">Account</p>
                  <p className="text-[11px] text-muted-foreground/65">{userPlan === 'pro' ? 'Pro · unlimited' : 'Free plan'}</p>
                </div>
                <ModeToggle />
              </div>
            )}
          </SignedIn>
          <SignedOut>
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SignInButton mode="modal">
                      <Button variant="ghost" size="icon" className="w-full h-9" aria-label="Sign in">
                        <CaretRight weight="bold" className="size-4" aria-hidden="true" />
                      </Button>
                    </SignInButton>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">Sign in</TooltipContent>
              </Tooltip>
            ) : (
              <div className="flex items-center gap-2">
                <SignInButton mode="modal">
                  <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} className="flex-1">
                    <Button
                      className="w-full h-10 font-semibold gap-1.5 bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white shadow-[0_4px_16px_rgba(59,91,217,0.3)] hover:shadow-[0_4px_20px_rgba(59,91,217,0.45)] transition-all"
                    >
                      Sign in to save
                    </Button>
                  </motion.div>
                </SignInButton>
                <ModeToggle />
              </div>
            )}
          </SignedOut>
        </div>

        {/* Dialogs */}
        <Dialog open={Boolean(renameSession)} onOpenChange={(open) => { if (!open) setRenameSession(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Rename chat</DialogTitle>
              <DialogDescription>Update how this conversation appears in your sidebar.</DialogDescription>
            </DialogHeader>
            <Input
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              placeholder="Chat title"
              maxLength={120}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleRenameSubmit(); } }}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameSession(null)}>Cancel</Button>
              <Button onClick={() => void handleRenameSubmit()} disabled={renaming || !renameTitle.trim()}>
                {renaming ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={Boolean(deleteSession)} onOpenChange={(open) => { if (!open) setDeleteSession(null); }}>
          <AlertDialogContent size="sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete chat?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteSession
                  ? `This will permanently remove "${deleteSession.title}" and its messages.`
                  : 'This will permanently remove this chat and its messages.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={() => void handleDeleteConfirm()}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Projects feature dialogs */}
        <Dialog open={createProjectOpen} onOpenChange={(open) => { if (!open) { setCreateProjectOpen(false); setNewProjectName(''); setNewProjectIcon(''); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New project</DialogTitle>
              <DialogDescription>Group related chats together. You can move chats in later.</DialogDescription>
            </DialogHeader>
            <div className="flex gap-2">
              <Input
                value={newProjectIcon}
                onChange={(e) => setNewProjectIcon(e.target.value)}
                placeholder="📁"
                maxLength={4}
                className="w-16 text-center"
                aria-label="Project icon (optional emoji)"
              />
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Project name"
                maxLength={80}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleCreateProjectSubmit(); } }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateProjectOpen(false)}>Cancel</Button>
              <Button onClick={() => void handleCreateProjectSubmit()} disabled={creatingProject || !newProjectName.trim()}>
                {creatingProject ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(editProject)} onOpenChange={(open) => { if (!open) setEditProject(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Rename project</DialogTitle>
              <DialogDescription>Update the name or icon shown in your sidebar.</DialogDescription>
            </DialogHeader>
            <div className="flex gap-2">
              <Input
                value={editProjectIcon}
                onChange={(e) => setEditProjectIcon(e.target.value)}
                placeholder="📁"
                maxLength={4}
                className="w-16 text-center"
                aria-label="Project icon (optional emoji)"
              />
              <Input
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                placeholder="Project name"
                maxLength={80}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleEditProjectSubmit(); } }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditProject(null)}>Cancel</Button>
              <Button onClick={() => void handleEditProjectSubmit()} disabled={savingProject || !editProjectName.trim()}>
                {savingProject ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={Boolean(deleteProjectTarget)} onOpenChange={(open) => { if (!open) setDeleteProjectTarget(null); }}>
          <AlertDialogContent size="sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete project?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteProjectTarget
                  ? `"${deleteProjectTarget.name}" will be removed. Its chats are NOT deleted — they'll move back to "All chats".`
                  : 'This project will be removed. Its chats are not deleted.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={() => void handleDeleteProjectConfirm()}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <UpgradeModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />

        {/* Right-edge resize affordance — visible when collapsed */}
        {collapsed && (
          <div
            onClick={() => setCollapsed(false)}
            className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/20 transition-colors z-10"
          />
        )}
      </motion.aside>
    </TooltipProvider>
  );
}
